import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";

const API_BASE = "/api/frota/fornecedores";

export default function FornecedoresTab() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ nome: "", ativo: true, observacoes: "" });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_BASE);
      if (!res.ok) throw new Error("Erro ao carregar fornecedores");
      const data = await res.json();
      setLista(Array.isArray(data) ? data : []);
    } catch (err) {
      toast({
        title: "Erro",
        description: err.message,
        status: "error",
        isClosable: true,
      });
      setLista([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditingId(null);
    setForm({ nome: "", ativo: true, observacoes: "" });
    onOpen();
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      nome: row.nome ?? "",
      ativo: row.ativo !== false,
      observacoes: row.observacoes ?? "",
    });
    onOpen();
  };

  const handleSave = async () => {
    if (!form.nome?.trim()) {
      toast({
        title: "Nome é obrigatório",
        status: "warning",
        isClosable: true,
      });
      return;
    }
    setSaving(true);
    try {
      const body = editingId ? { ...form, id: editingId } : form;
      const res = await fetch(API_BASE, {
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erro ao salvar");
      toast({
        title: "Salvo com sucesso",
        status: "success",
        isClosable: true,
      });
      onClose();
      load();
    } catch (err) {
      toast({
        title: "Erro",
        description: err.message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este fornecedor?")) return;
    try {
      const res = await fetch(`${API_BASE}?id=${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Erro ao excluir");
      toast({ title: "Excluído", status: "success", isClosable: true });
      load();
    } catch (err) {
      toast({
        title: "Erro",
        description: err.message,
        status: "error",
        isClosable: true,
      });
    }
  };

  return (
    <Box>
      <Flex
        align="center"
        justify="space-between"
        mb={6}
        flexWrap="wrap"
        gap={4}
      >
        <Heading size="md" color="#007abe">
          Fornecedores
        </Heading>
        <Button
          leftIcon={<FaPlus />}
          bg="#007abe"
          color="white"
          onClick={openNew}
          _hover={{ bg: "#0066a3" }}
        >
          Novo fornecedor
        </Button>
      </Flex>
      <TableContainer
        bg="white"
        borderRadius="xl"
        boxShadow="md"
        overflowX="auto"
      >
        {loading ? (
          <Center py={12}>
            <Spinner size="xl" color="#007abe" />
          </Center>
        ) : (
          <Table variant="simple" size="sm">
            <Thead bg="#007abe">
              <Tr>
                <Th color="white">Nome</Th>
                <Th color="white">Ativo</Th>
                <Th color="white">Observações</Th>
                <Th color="white" width="120px">
                  Ações
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {lista.map((row) => (
                <Tr
                  key={row.id}
                  opacity={row.ativo === false ? 0.65 : 1}
                  bg={row.ativo === false ? "gray.100" : undefined}
                  _hover={row.ativo === false ? { bg: "gray.200" } : undefined}
                >
                  <Td fontWeight="medium">{row.nome}</Td>
                  <Td>{row.ativo ? "Sim" : "Não"}</Td>
                  <Td maxW="200px" isTruncated title={row.observacoes || ""}>
                    {row.observacoes || "–"}
                  </Td>
                  <Td>
                    <Flex gap={2}>
                      <Button
                        size="xs"
                        leftIcon={<FaEdit />}
                        onClick={() => openEdit(row)}
                      >
                        Editar
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="red"
                        leftIcon={<FaTrash />}
                        onClick={() => handleDelete(row.id)}
                      >
                        Excluir
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
        {!loading && lista.length === 0 && (
          <Center py={8} color="gray.500">
            Nenhum fornecedor cadastrado. Clique em &quot;Novo fornecedor&quot;
            para cadastrar.
          </Center>
        )}
      </TableContainer>

      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingId ? "Editar fornecedor" : "Novo fornecedor"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Nome</FormLabel>
              <Input
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Ex: VIBRA, IPIRANGA, RAIZEN"
              />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Observações</FormLabel>
              <Input
                value={form.observacoes}
                onChange={(e) =>
                  setForm({ ...form, observacoes: e.target.value })
                }
              />
            </FormControl>
            <FormControl display="flex" alignItems="center" mt={4}>
              <FormLabel mb={0}>Ativo</FormLabel>
              <Switch
                isChecked={form.ativo}
                onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button
              bg="#007abe"
              color="white"
              onClick={handleSave}
              isLoading={saving}
              _hover={{ bg: "#0066a3" }}
            >
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
